import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useProperty } from "../../../../components/property-provider";
import { IssueCode } from "../../domain/entities/ValidationIssue";
import { ExtraChange, useExtraImpactApi } from "../../infrastructure/api/extraImpact.api";
import { GrpcIssues } from "../../infrastructure/grpcIssues";
import {
  ExtraChangeBlockedView,
  ExtraChangeContext,
  ExtraChangeDialogsProps,
  ExtraImpactDialogView,
  ExtraImpactDialogViewModel,
} from "../../presentation/components/ExtraImpactDialog";
import { useConfirmDialog } from "../../presentation/hooks/useConfirmDialog";
import { useExtrasCategoriesFlag } from "./useExtrasCategoriesFlag";
import { useIssueFormatter } from "./useIssueFormatter";

/**
 * - `proceed`: nothing was shown, the caller goes on as today (flag off, or no impact).
 * - `confirmed`: the user pressed *Continue* in the impact dialog.
 * - `abort`: the user cancelled, or the change is blocked (it would leave a limits block empty).
 */
export type ExtraChangeDecision = "proceed" | "confirmed" | "abort";

/**
 * The impact check before `UpdateExtra` and `DeleteExtra` (skipped with the flag off), its dialogs,
 * and the change-blocked message for a `FAILED_PRECONDITION` rejection. Render
 * `ExtraChangeDialogs` with `dialogs`. A failing check rejects `guard`, so the caller shows its generic error.
 */
export function useExtraChangeGuard() {
  const flag = useExtrasCategoriesFlag();
  const intl = useIntl();
  const formatter = useIssueFormatter();
  const { ticker } = useProperty();
  const { checkExtraChangeImpact } = useExtraImpactApi();
  const { open: openImpact, dialog: impact } = useConfirmDialog<ExtraImpactDialogView>();
  const { open: openBlocked, dialog: blocked } = useConfirmDialog<ExtraChangeBlockedView>();

  const viewModel = useMemo(() => new ExtraImpactDialogViewModel(intl, formatter, ticker), [
    formatter,
    intl,
    ticker,
  ]);

  const guard = useCallback(
    async (change: ExtraChange, context: ExtraChangeContext): Promise<ExtraChangeDecision> => {
      if (!flag) {
        return "proceed";
      }
      const result = await checkExtraChangeImpact(change, intl.locale);
      if (result.isBlocked()) {
        await openBlocked(viewModel.blockedByImpacts(result.blockingImpacts(), context));
        return "abort";
      }
      const warnings = result.warningImpacts();
      if (warnings.length === 0) {
        return "proceed";
      }
      return (await openImpact(viewModel.confirmation(warnings, context))) ? "confirmed" : "abort";
    },
    [checkExtraChangeImpact, flag, intl.locale, openBlocked, openImpact, viewModel]
  );

  /**
   * Shows the change-blocked message when `error` is a `FAILED_PRECONDITION` with
   * `EXTRA_CHANGE_LEAVES_BLOCK_EMPTY`, whatever the flag. It returns at once, without waiting for *Close*, so that it can be called
   * from inside another dialog that is about to close.
   */
  const showRejection = useCallback(
    (error: unknown, context: ExtraChangeContext): boolean => {
      const rejection = GrpcIssues.read(error);
      if (
        !rejection?.isFailedPrecondition() ||
        !rejection.find(IssueCode.EXTRA_CHANGE_LEAVES_BLOCK_EMPTY)
      ) {
        return false;
      }
      void openBlocked(viewModel.blockedByIssues(rejection.issues, context));
      return true;
    },
    [openBlocked, viewModel]
  );

  const dialogs = useMemo<ExtraChangeDialogsProps>(() => ({ impact, blocked }), [blocked, impact]);

  return { guard, showRejection, dialogs };
}
